import {
  Box,
  Flex,
  Grid,
  HStack,
  Heading,
  Spinner,
  Text,
  VStack,
} from "@chakra-ui/react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router"
import { useContext, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { useTranslation } from "react-i18next"
import {
  FiArrowLeft,
  FiArrowRight,
  FiCheckCircle,
  FiCopy,
  FiKey,
  FiLock,
  FiMail,
  FiMaximize2,
  FiRefreshCw,
  FiSmartphone,
  FiTrash2,
  FiWifi,
} from "react-icons/fi"

import { QRCodeSVG } from "qrcode.react"

import { OpenAPI, ReadersService } from "@/client"
import type {
  ReaderCreate,
  ReaderModelEnum,
  ReaderRoleEnum,
  ReaderStateEnum,
  ReaderUpdate,
} from "@/client"
import ConfirmDialog from "@/components/Common/ConfirmDialog"
import {
  GradientBorder,
  IconBox,
  VisionButton,
  VisionCard,
  VisionInput,
} from "@/components/VisionUI"
import { Badge } from "@/components/ui/badge"
import {
  DialogBackdrop,
  DialogBody,
  DialogCloseTrigger,
  DialogContent,
  DialogRoot,
} from "@/components/ui/dialog"
import { Field } from "@/components/ui/field"
import { OrganizationContext, isAllOrgs } from "@/contexts/OrganizationContext"
import useCustomToast from "@/hooks/useCustomToast"
import { useSemanticColors } from "@/hooks/useSemanticColors"

// VITE_MQTT_HOST, VITE_MQTT_PORT, VITE_MQTT_WSS_PORT must be injected at Docker
// build time via build-and-push.sh. If VITE_MQTT_HOST is absent, it is derived
// from VITE_API_URL by swapping the "api." subdomain for "mqtt." so that any
// environment works without a hardcoded hostname.
const MQTT_BROKER_HOST =
  import.meta.env.VITE_MQTT_HOST ||
  (import.meta.env.VITE_API_URL ?? "")
    .replace(/^https?:\/\//, "")
    .replace(/^api\./, "mqtt.")
    .split("/")[0]
const MQTT_BROKER_PORT = Number(import.meta.env.VITE_MQTT_PORT || 8883)
const MQTT_BROKER_WSS_PORT = Number(import.meta.env.VITE_MQTT_WSS_PORT || 443)

export const Route = createFileRoute("/_layout/reader-form")({
  component: ReaderFormRoute,
  validateSearch: (
    search: Record<string, unknown>,
  ): { readerId?: string; requestId?: string } => {
    return {
      readerId: search.readerId as string | undefined,
      // When present, render the 3-step license→reader→QR provisioning wizard
      // (launched from a pending license request on /licenses).
      requestId: search.requestId as string | undefined,
    }
  },
})

interface FormData {
  name: string
  device_model: ReaderModelEnum
  desired_state: ReaderStateEnum
  role: ReaderRoleEnum
  mac_address: string | undefined
  serial_number: string | undefined
  lat: number
  lon: number
  inventory_interval_seconds: number | null
}

const deviceModels: { value: ReaderModelEnum; label: string }[] = [
  { value: "AR", label: "Android Reader" },
  // QDATDROID / TAPDPP_* are backend enum values; the cast keeps the build
  // green until the OpenAPI client is regenerated (npm run generate-client)
  // post-deploy.
  { value: "QDATDROID" as ReaderModelEnum, label: "QDatDroid" },
  { value: "TAPDPP_IOS" as ReaderModelEnum, label: "TapDPP-iOS" },
  { value: "TAPDPP_ANDROID" as ReaderModelEnum, label: "TapDPP-Android" },
  { value: "FX7500", label: "Zebra FX7500" },
  { value: "FX9600", label: "Zebra FX9600" },
]

const MAC_REQUIRED_MODELS: ReaderModelEnum[] = ["FX7500", "FX9600"]

// Only operator-settable states. ERROR and DATADUMP are system-driven and
// only ever appear on observed_state; offering them in the form would let
// the operator put the reader into an inconsistent state.
const readerStates: { value: ReaderStateEnum; label: string }[] = [
  { value: "STANDBY", label: "Standby" },
  { value: "INVENTORY", label: "Inventory" },
  { value: "CONFIGURATION", label: "Configuration" },
  { value: "MAINTENANCE", label: "Maintenance" },
]

const DEFAULT_INVENTORY_INTERVAL_SECONDS = 300
const MIN_INVENTORY_INTERVAL_SECONDS = 30

const readerRoles: { value: ReaderRoleEnum; label: string }[] = [
  { value: "INITIALIZER", label: "Initializer" },
  { value: "INVENTORY", label: "Inventory" },
  { value: "COLLECTOR", label: "Collector" },
  { value: "MONITOR", label: "Monitor" },
]

// Route dispatcher: a pending license request (requestId) launches the 3-step
// provisioning wizard; otherwise the normal reader create/edit form renders.
// Keeping these as two distinct components ensures stable hook ordering.
function ReaderFormRoute() {
  const { requestId } = useSearch({ from: "/_layout/reader-form" })
  if (requestId) return <ProvisionDeviceWizard requestId={requestId} />
  return <ReaderForm />
}

function ReaderForm() {
  const { t } = useTranslation(["readers", "common"])
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const showToast = useCustomToast()
  const sc = useSemanticColors()
  const { readerId } = useSearch({ from: "/_layout/reader-form" })
  const { currentOrganization } = useContext(OrganizationContext)
  // After a successful create we want the form to behave as if it were
  // editing the just-created reader, without losing the one-time
  // generatedCredentials state to a remount. Track an in-component id that
  // takes effect when the URL hasn't been updated yet (e.g. immediately
  // after a chained create→generate flow).
  const [localReaderId, setLocalReaderId] = useState<string | undefined>(
    undefined,
  )
  const activeReaderId = readerId ?? localReaderId
  const isEditMode = !!activeReaderId
  const [generatedCredentials, setGeneratedCredentials] = useState<any>(null)
  const [confirmRegenerate, setConfirmRegenerate] = useState(false)
  const [confirmRevoke, setConfirmRevoke] = useState(false)
  const [fullscreenQrValue, setFullscreenQrValue] = useState<string | null>(
    null,
  )

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    defaultValues: {
      desired_state: "STANDBY",
      role: "INVENTORY",
      device_model: "AR",
      lat: 0,
      lon: 0,
      inventory_interval_seconds: null,
    },
  })

  const watchedModel = watch("device_model")
  const requiresMac = MAC_REQUIRED_MODELS.includes(watchedModel)

  // Clear MAC address when switching to a model that doesn't use it
  useEffect(() => {
    if (!requiresMac) {
      setValue("mac_address", undefined)
    }
  }, [requiresMac, setValue])

  // Fetch reader data if in edit mode (or transitioned-to-edit-mode after
  // a successful create — activeReaderId covers both cases).
  const { data: reader, isLoading: readerLoading } = useQuery({
    queryKey: ["reader", activeReaderId],
    queryFn: () => ReadersService.readReader({ id: activeReaderId! }),
    enabled: isEditMode,
  })

  // Reset form when reader data is loaded
  useEffect(() => {
    if (reader) {
      reset({
        name: reader.name,
        device_model: reader.device_model,
        desired_state: reader.desired_state,
        role: reader.role,
        mac_address: reader.mac_address ?? undefined,
        serial_number: (reader as any).serial_number ?? undefined,
        lat: reader.lat,
        lon: reader.lon,
        inventory_interval_seconds: reader.inventory_interval_seconds ?? null,
      })
    }
  }, [reader, reset])

  // Create + auto-provision MQTT credentials in one click. The two backend
  // endpoints are independent, so we chain them client-side and tolerate a
  // partial failure (reader created but credential generation failed) by
  // routing the user into edit mode where Regenerate is the recovery path.
  const createReaderMutation = useMutation({
    mutationFn: async (data: ReaderCreate) => {
      const reader = await ReadersService.createReader({ requestBody: data })
      try {
        const creds = await ReadersService.generateMqttCredentials({
          id: reader.id,
        })
        return { reader, creds, credentialError: null as any }
      } catch (credentialError) {
        return { reader, creds: null, credentialError }
      }
    },
    onSuccess: ({ reader, creds, credentialError }) => {
      // Transition the form into edit mode for the just-created reader
      // before showing the credentials so React doesn't unmount the
      // generatedCredentials state.
      setLocalReaderId(reader.id)
      if (creds) {
        setGeneratedCredentials(creds)
      }
      queryClient.invalidateQueries({ queryKey: ["readers"] })
      queryClient.invalidateQueries({ queryKey: ["reader", reader.id] })
      queryClient.invalidateQueries({
        queryKey: ["mqtt-credentials-status", reader.id],
      })
      // Reflect the new id in the URL so a refresh keeps the user in edit
      // mode (the one-time password is gone after refresh, but the
      // credential status query will repopulate the panel).
      navigate({
        to: "/reader-form",
        search: { readerId: reader.id },
        replace: true,
      })
      if (credentialError) {
        const detail =
          credentialError?.body?.detail ||
          credentialError?.message ||
          "Failed to generate MQTT credentials"
        showToast.showErrorToast(
          `${t("readers:mqtt.createdNoCredentials")} (${detail})`,
        )
      } else {
        showToast.showSuccessToast(t("readers:mqtt.createdAndProvisioned"))
      }
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to create reader"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const updateReaderMutation = useMutation({
    mutationFn: async (data: ReaderUpdate) => {
      const result = await ReadersService.updateReader({
        id: activeReaderId!,
        requestBody: data,
      })
      return result
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["readers"] })
      queryClient.invalidateQueries({ queryKey: ["reader", activeReaderId] })
      showToast.showSuccessToast(t("readers:form.updated"))
      navigate({ to: "/readers" })
    },
    onError: (err: any) => {
      const errDetail =
        err.body?.detail || err.message || "Failed to update reader"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const handleFormSubmit = (data: FormData) => {
    if (!currentOrganization || isAllOrgs(currentOrganization)) {
      showToast.showErrorToast(t("common:org.selectSpecific"))
      return
    }

    const mac = data.mac_address || undefined
    // Only persist a scheduler interval when desired_state is INVENTORY —
    // it is the only state where the dispatcher reads it.
    const interval =
      data.desired_state === "INVENTORY"
        ? Math.max(
            data.inventory_interval_seconds ??
              DEFAULT_INVENTORY_INTERVAL_SECONDS,
            MIN_INVENTORY_INTERVAL_SECONDS,
          )
        : null

    const serial = data.serial_number?.trim() || undefined

    if (isEditMode) {
      updateReaderMutation.mutate({
        name: data.name,
        device_model: data.device_model,
        desired_state: data.desired_state,
        role: data.role,
        mac_address: mac,
        serial_number: serial,
        lat: data.lat,
        lon: data.lon,
        inventory_interval_seconds: interval,
      } as any)
    } else {
      createReaderMutation.mutate({
        ...data,
        mac_address: mac,
        serial_number: serial,
        organisation_id: currentOrganization.id,
        inventory_interval_seconds: interval,
      } as any)
    }
  }

  // MQTT credential status query
  const { data: mqttStatus, isLoading: mqttStatusLoading } = useQuery({
    queryKey: ["mqtt-credentials-status", activeReaderId],
    queryFn: () =>
      ReadersService.getMqttCredentialStatus({ id: activeReaderId! }),
    enabled: isEditMode,
  })

  // Note: there is no standalone "generate credentials" mutation here.
  // First-time provisioning happens automatically as part of
  // createReaderMutation (above). If creds end up absent later (e.g. after
  // a Revoke), Regenerate is the way back — its server-side revoke step is
  // a no-op when there's nothing to revoke, so it works as both rotate and
  // generate-when-empty.

  // Revoke credentials mutation
  const revokeCredentialsMutation = useMutation({
    mutationFn: () =>
      ReadersService.revokeMqttCredentials({ id: activeReaderId! }),
    onSuccess: () => {
      setGeneratedCredentials(null)
      queryClient.invalidateQueries({
        queryKey: ["mqtt-credentials-status", activeReaderId],
      })
      queryClient.invalidateQueries({ queryKey: ["reader", activeReaderId] })
      showToast.showSuccessToast(t("readers:mqtt.revoked"))
    },
    onError: (err: any) => {
      const errDetail = err.body?.detail || "Failed to revoke credentials"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  // Regenerate credentials mutation
  const regenerateCredentialsMutation = useMutation({
    mutationFn: () =>
      ReadersService.regenerateMqttCredentials({ id: activeReaderId! }),
    onSuccess: (data) => {
      setGeneratedCredentials(data)
      queryClient.invalidateQueries({
        queryKey: ["mqtt-credentials-status", activeReaderId],
      })
      queryClient.invalidateQueries({ queryKey: ["reader", activeReaderId] })
      showToast.showSuccessToast(t("readers:mqtt.regenerated"))
    },
    onError: (err: any) => {
      const errDetail = err.body?.detail || "Failed to regenerate credentials"
      showToast.showErrorToast(`${errDetail}`)
    },
  })

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text)
    showToast.showSuccessToast(t("readers:mqtt.copied", { label }))
  }

  const buildDeviceConfigJson = (creds: {
    username: string
    password?: string
    mqtt_topic_id: string
    topics: Record<string, string>
  }) => {
    return JSON.stringify({
      mqtt: {
        host: MQTT_BROKER_HOST,
        port: MQTT_BROKER_PORT,
        wss_port: MQTT_BROKER_WSS_PORT,
        tls: true,
        username: creds.username,
        ...(creds.password && { password: creds.password }),
      },
      device: {
        topic_id: creds.mqtt_topic_id,
        topics: creds.topics,
      },
    })
  }

  if (!currentOrganization) {
    return (
      <VisionCard>
        <Heading size="md" color={sc.heading} mb={4}>
          {t("common:org.noOrg")}
        </Heading>
        <Text color={sc.textMuted}>{t("common:org.selectPrompt")}</Text>
      </VisionCard>
    )
  }

  if (isEditMode && readerLoading) {
    return (
      <Flex justify="center" align="center" height="60vh">
        <Spinner size="xl" color={sc.spinnerColor} />
      </Flex>
    )
  }

  return (
    <Box>
      <Flex align="center" gap={4} mb={8}>
        <Box flex={1}>
          <Heading size="xl" color={sc.heading}>
            {isEditMode
              ? t("readers:form.editTitle")
              : t("readers:form.addTitle")}
          </Heading>
          <Text color={sc.textMuted} mt={1}>
            {isEditMode
              ? t("readers:form.editSubtitle")
              : t("readers:form.addSubtitle")}
          </Text>
        </Box>
        <VisionButton
          variant="ghost"
          onClick={() => navigate({ to: "/readers" })}
          size="sm"
        >
          <FiArrowLeft />
          {t("common:actions.back")}
        </VisionButton>
      </Flex>

      <Grid templateColumns={{ base: "1fr", lg: "3fr 1fr" }} gap={6}>
        <Box>
          <GradientBorder borderRadius="20px" p={0}>
            <VisionCard variant="elevated" p={8} borderRadius="20px">
              <Flex align="center" gap={3} mb={6}>
                <IconBox variant="glass" size="md">
                  <FiWifi fontSize="20px" color={sc.iconColor} />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("readers:form.configTitle")}
                </Heading>
              </Flex>

              <VStack
                gap={6}
                as="form"
                onSubmit={handleSubmit(handleFormSubmit)}
              >
                <Grid
                  templateColumns={{ base: "1fr", md: "repeat(2, 1fr)" }}
                  gap={4}
                  w="100%"
                >
                  <Field
                    required
                    invalid={!!errors.name}
                    errorText={errors.name?.message}
                    gridColumn={!requiresMac ? { md: "span 2" } : undefined}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.name")}
                    </Text>
                    <VisionInput
                      {...register("name", {
                        required: t("readers:form.nameRequired"),
                        minLength: {
                          value: 2,
                          message: "Name must be at least 2 characters",
                        },
                      })}
                      placeholder={t("readers:form.namePlaceholder")}
                      size="lg"
                      variant="glass"
                    />
                  </Field>

                  <Field
                    invalid={!!errors.mac_address}
                    errorText={errors.mac_address?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.mac")}{" "}
                      <Text
                        as="span"
                        color={sc.textMuted}
                        fontSize="xs"
                        fontWeight="400"
                      >
                        {t("readers:form.macOptional")}
                      </Text>
                    </Text>
                    <Text color={sc.textMuted} fontSize="xs" mb={2}>
                      {t("readers:form.macHelp")}
                    </Text>
                    <VisionInput
                      {...register("mac_address", {
                        validate: (value) => {
                          if (!value) return true
                          return (
                            /^([0-9A-F]{2}:){5}[0-9A-F]{2}$/.test(value) ||
                            t("readers:form.macInvalid")
                          )
                        },
                      })}
                      placeholder="AA:BB:CC:DD:EE:FF (optional)"
                      size="lg"
                      variant="glass"
                      maxLength={17}
                      onChange={(e) => {
                        const raw = e.target.value
                          .replace(/[^0-9A-Fa-f]/g, "")
                          .toUpperCase()
                        const parts = raw.match(/.{1,2}/g) || []
                        const formatted = parts.slice(0, 6).join(":")
                        setValue("mac_address", formatted, {
                          shouldValidate: !!formatted,
                        })
                      }}
                    />
                  </Field>

                  <Field>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.serialNumber")}{" "}
                      <Text
                        as="span"
                        color={sc.textMuted}
                        fontSize="xs"
                        fontWeight="400"
                      >
                        {t("readers:form.serialOptional")}
                      </Text>
                    </Text>
                    <Text color={sc.textMuted} fontSize="xs" mb={2}>
                      {t("readers:form.serialHelp")}
                    </Text>
                    <VisionInput
                      {...register("serial_number")}
                      placeholder="SN-..."
                      size="lg"
                      variant="glass"
                      maxLength={100}
                    />
                  </Field>

                  <Field required>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.model")}
                    </Text>
                    <Box
                      as="select"
                      {...register("device_model", { required: true })}
                      p={3}
                      borderRadius="12px"
                      bg={sc.eventBg}
                      backdropFilter="blur(20px)"
                      border="1px solid rgba(255, 255, 255, 0.2)"
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      w="100%"
                      transition="all 0.2s"
                      _hover={{
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        bg: sc.hoverBg,
                      }}
                      _focus={{
                        outline: "none",
                        borderColor: "brand.200",
                        boxShadow: "0 0 0 1px rgba(52, 161, 221, 0.6)",
                        bg: sc.activeBg,
                      }}
                    >
                      {deviceModels.map((model) => (
                        <option key={model.value} value={model.value}>
                          {t(`readers:models.${model.value}`)}
                        </option>
                      ))}
                    </Box>
                  </Field>

                  <Field required>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.role")}
                    </Text>
                    <Box
                      as="select"
                      {...register("role", { required: true })}
                      p={3}
                      borderRadius="12px"
                      bg={sc.eventBg}
                      backdropFilter="blur(20px)"
                      border="1px solid rgba(255, 255, 255, 0.2)"
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      w="100%"
                      transition="all 0.2s"
                      _hover={{
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        bg: sc.hoverBg,
                      }}
                      _focus={{
                        outline: "none",
                        borderColor: "brand.200",
                        boxShadow: "0 0 0 1px rgba(52, 161, 221, 0.6)",
                        bg: sc.activeBg,
                      }}
                    >
                      {readerRoles.map((role) => (
                        <option key={role.value} value={role.value}>
                          {t(`readers:roles.${role.value}`)}
                        </option>
                      ))}
                    </Box>
                  </Field>

                  <Field required>
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.desiredState")}
                    </Text>
                    <Box
                      as="select"
                      {...register("desired_state", { required: true })}
                      p={3}
                      borderRadius="12px"
                      bg={sc.eventBg}
                      backdropFilter="blur(20px)"
                      border="1px solid rgba(255, 255, 255, 0.2)"
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      w="100%"
                      transition="all 0.2s"
                      _hover={{
                        borderColor: "rgba(255, 255, 255, 0.3)",
                        bg: sc.hoverBg,
                      }}
                      _focus={{
                        outline: "none",
                        borderColor: "brand.200",
                        boxShadow: "0 0 0 1px rgba(52, 161, 221, 0.6)",
                        bg: sc.activeBg,
                      }}
                    >
                      {readerStates.map((state) => (
                        <option key={state.value} value={state.value}>
                          {t(`readers:states.${state.value}`)}
                        </option>
                      ))}
                    </Box>
                  </Field>

                  {watch("desired_state") === "INVENTORY" ? (
                    <Field>
                      <Text
                        color={sc.heading}
                        fontSize="sm"
                        fontWeight="500"
                        mb={2}
                      >
                        {t("readers:form.inventoryInterval")}
                      </Text>
                      <VisionInput
                        {...register("inventory_interval_seconds", {
                          valueAsNumber: true,
                          min: MIN_INVENTORY_INTERVAL_SECONDS,
                        })}
                        type="number"
                        min={MIN_INVENTORY_INTERVAL_SECONDS}
                        step={10}
                        placeholder={String(DEFAULT_INVENTORY_INTERVAL_SECONDS)}
                        size="lg"
                        variant="glass"
                      />
                      <Text color={sc.textMuted} fontSize="xs" mt={1}>
                        {t("readers:form.inventoryIntervalHelp")}
                      </Text>
                    </Field>
                  ) : (
                    <Box />
                  )}

                  <Field
                    required
                    invalid={!!errors.lat}
                    errorText={errors.lat?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.lat")}
                    </Text>
                    <VisionInput
                      {...register("lat", {
                        required: t("readers:form.latRequired"),
                        valueAsNumber: true,
                        validate: (value) =>
                          (value >= -90 && value <= 90) ||
                          t("readers:form.latInvalid"),
                      })}
                      placeholder="0.000000"
                      size="lg"
                      variant="glass"
                      type="number"
                      step="0.000001"
                    />
                  </Field>

                  <Field
                    required
                    invalid={!!errors.lon}
                    errorText={errors.lon?.message}
                  >
                    <Text
                      color={sc.heading}
                      fontSize="sm"
                      fontWeight="500"
                      mb={2}
                    >
                      {t("readers:form.lon")}
                    </Text>
                    <VisionInput
                      {...register("lon", {
                        required: t("readers:form.lonRequired"),
                        valueAsNumber: true,
                        validate: (value) =>
                          (value >= -180 && value <= 180) ||
                          t("readers:form.lonInvalid"),
                      })}
                      placeholder="0.000000"
                      size="lg"
                      variant="glass"
                      type="number"
                      step="0.000001"
                    />
                  </Field>
                </Grid>

                <HStack gap={3} w="100%">
                  <VisionButton
                    type="button"
                    variant="ghost"
                    size="lg"
                    onClick={() => navigate({ to: "/readers" })}
                  >
                    {t("common:actions.cancel")}
                  </VisionButton>
                  <VisionButton
                    type="submit"
                    variant="brand"
                    size="lg"
                    flex="1"
                    loading={
                      createReaderMutation.isPending ||
                      updateReaderMutation.isPending
                    }
                  >
                    {createReaderMutation.isPending ||
                    updateReaderMutation.isPending
                      ? isEditMode
                        ? t("common:status.updating")
                        : t("common:status.creating")
                      : isEditMode
                        ? t("readers:form.updateReader")
                        : t("readers:form.createReader")}
                  </VisionButton>
                </HStack>
              </VStack>
            </VisionCard>
          </GradientBorder>
        </Box>

        <Box>
          {/* MQTT Security — only in edit mode */}
          {isEditMode && activeReaderId && (
            <VisionCard>
              <Flex align="center" gap={3} mb={6}>
                <IconBox variant="glass" size="md">
                  <FiLock fontSize="20px" color={sc.iconColor} />
                </IconBox>
                <Heading size="lg" color={sc.heading}>
                  {t("readers:mqtt.title")}
                </Heading>
              </Flex>

              {mqttStatusLoading && (
                <Flex justify="center" py={8}>
                  <Spinner size="lg" color={sc.spinnerColor} />
                </Flex>
              )}

              {/* Just-generated credentials — show once */}
              {generatedCredentials && (
                <Box
                  mb={6}
                  p={4}
                  borderRadius="8px"
                  bg="rgba(255, 152, 0, 0.1)"
                  border="1px solid rgba(255, 152, 0, 0.4)"
                >
                  <Text color="orange.300" fontWeight="bold" mb={3}>
                    {t("readers:mqtt.saveWarning")}
                  </Text>
                  <VStack align="stretch" gap={3}>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                      >
                        {t("readers:mqtt.username")}
                      </Text>
                      <HStack>
                        <Text
                          color={sc.heading}
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          {generatedCredentials.username}
                        </Text>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              generatedCredentials.username,
                              t("readers:mqtt.username"),
                            )
                          }
                        >
                          <FiCopy />
                        </VisionButton>
                      </HStack>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                      >
                        {t("readers:mqtt.password")}
                      </Text>
                      <HStack>
                        <Text
                          color={sc.heading}
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          {generatedCredentials.password}
                        </Text>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              generatedCredentials.password,
                              t("readers:mqtt.password"),
                            )
                          }
                        >
                          <FiCopy />
                        </VisionButton>
                      </HStack>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                      >
                        {t("readers:mqtt.topicId")}
                      </Text>
                      <HStack>
                        <Text
                          color={sc.heading}
                          fontFamily="mono"
                          fontSize="sm"
                        >
                          {generatedCredentials.mqtt_topic_id}
                        </Text>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            copyToClipboard(
                              generatedCredentials.mqtt_topic_id,
                              t("readers:mqtt.topicId"),
                            )
                          }
                        >
                          <FiCopy />
                        </VisionButton>
                      </HStack>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                      >
                        {t("readers:mqtt.topics")}
                      </Text>
                      {generatedCredentials.topics &&
                        Object.entries(generatedCredentials.topics).map(
                          ([key, value]) => (
                            <Text
                              key={key}
                              color={sc.textSecondary}
                              fontFamily="mono"
                              fontSize="xs"
                            >
                              {key}: {value as string}
                            </Text>
                          ),
                        )}
                    </Box>

                    {/* QR Code for device provisioning */}
                    <Box
                      mt={4}
                      p={4}
                      borderRadius="8px"
                      bg="white"
                      display="flex"
                      flexDirection="column"
                      alignItems="center"
                      gap={3}
                    >
                      <QRCodeSVG
                        value={buildDeviceConfigJson(generatedCredentials)}
                        size={220}
                        level="M"
                      />
                      <Text fontSize="xs" color="gray.600" textAlign="center">
                        {t("readers:mqtt.qrScanWithPassword")}
                      </Text>
                      <VisionButton
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setFullscreenQrValue(
                            buildDeviceConfigJson(generatedCredentials),
                          )
                        }
                      >
                        <FiMaximize2 /> Click to expand
                      </VisionButton>
                    </Box>
                  </VStack>
                </Box>
              )}

              {/* No credentials state — only reachable after a Revoke,
                  since reader creation now provisions credentials. The
                  recovery path is Regenerate (which works on a credential-
                  less reader because its server-side revoke step is a
                  no-op). */}
              {!mqttStatusLoading &&
                mqttStatus &&
                !mqttStatus.has_credentials &&
                !generatedCredentials && (
                  <VStack gap={4} align="stretch">
                    <Flex direction="column" align="center" py={8}>
                      <IconBox variant="glass" size="lg" mb={4}>
                        <FiLock fontSize="24px" color={sc.iconColor} />
                      </IconBox>
                      <Text color={sc.textMuted} textAlign="center" mb={4}>
                        {t("readers:mqtt.emptyStateAfterRevoke")}
                      </Text>
                      <VisionButton
                        variant="brand"
                        onClick={() => regenerateCredentialsMutation.mutate()}
                        loading={regenerateCredentialsMutation.isPending}
                      >
                        <FiRefreshCw /> {t("readers:mqtt.regenerate")}
                      </VisionButton>
                    </Flex>
                  </VStack>
                )}

              {/* Active credentials state */}
              {!mqttStatusLoading &&
                mqttStatus &&
                mqttStatus.has_credentials && (
                  <VStack gap={4} align="stretch">
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("readers:mqtt.username")}
                      </Text>
                      <Text
                        color={sc.heading}
                        fontFamily="mono"
                        fontSize="sm"
                        mt={1}
                      >
                        {mqttStatus.username}
                      </Text>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("readers:mqtt.topicId")}
                      </Text>
                      <Text
                        color={sc.heading}
                        fontFamily="mono"
                        fontSize="sm"
                        mt={1}
                      >
                        {mqttStatus.mqtt_topic_id}
                      </Text>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("readers:mqtt.created")}
                      </Text>
                      <Text color={sc.heading} fontSize="sm" mt={1}>
                        {mqttStatus.created_at
                          ? new Date(mqttStatus.created_at).toLocaleString()
                          : "—"}
                      </Text>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("readers:mqtt.status")}
                      </Text>
                      <Badge
                        colorScheme={mqttStatus.active ? "green" : "gray"}
                        size="sm"
                        mt={1}
                      >
                        {mqttStatus.active
                          ? t("common:status.active")
                          : t("common:status.inactive")}
                      </Badge>
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                        mb={1}
                      >
                        {t("readers:mqtt.topics")}
                      </Text>
                      {mqttStatus.topics &&
                        Object.entries(mqttStatus.topics).map(
                          ([key, value]) => (
                            <Text
                              key={key}
                              color={sc.textSecondary}
                              fontFamily="mono"
                              fontSize="xs"
                            >
                              {key}: {value as string}
                            </Text>
                          ),
                        )}
                    </Box>
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                        mb={2}
                      >
                        {t("readers:mqtt.qrTitle")}
                      </Text>
                      <Text fontSize="xs" color={sc.textSubtle} mb={3}>
                        {t("readers:mqtt.qrScan")}
                      </Text>
                      <Flex direction="column" align="center" gap={2}>
                        <Box bg="white" p={3} borderRadius="md">
                          <QRCodeSVG
                            value={buildDeviceConfigJson({
                              username: mqttStatus.username!,
                              password: mqttStatus.password!,
                              mqtt_topic_id: mqttStatus.mqtt_topic_id!,
                              topics: mqttStatus.topics!,
                            })}
                            size={180}
                          />
                        </Box>
                        <VisionButton
                          variant="ghost"
                          size="sm"
                          onClick={() =>
                            setFullscreenQrValue(
                              buildDeviceConfigJson({
                                username: mqttStatus.username!,
                                password: mqttStatus.password!,
                                mqtt_topic_id: mqttStatus.mqtt_topic_id!,
                                topics: mqttStatus.topics!,
                              }),
                            )
                          }
                        >
                          <FiMaximize2 /> Click to expand
                        </VisionButton>
                      </Flex>
                    </Box>
                    <HStack gap={3} mt={4}>
                      <VisionButton
                        variant="dark"
                        onClick={() => setConfirmRegenerate(true)}
                        loading={regenerateCredentialsMutation.isPending}
                      >
                        <FiRefreshCw /> {t("readers:mqtt.regenerate")}
                      </VisionButton>
                      <VisionButton
                        variant="ghost"
                        colorPalette="red"
                        onClick={() => setConfirmRevoke(true)}
                        loading={revokeCredentialsMutation.isPending}
                      >
                        <FiTrash2 /> {t("readers:mqtt.revoke")}
                      </VisionButton>
                    </HStack>
                  </VStack>
                )}
            </VisionCard>
          )}

          {/* Marketing consent — read-only audit record captured during
              TapDPP demo self-provisioning. Only shown when a consent
              decision was recorded for this reader. */}
          {isEditMode &&
            activeReaderId &&
            (reader as any)?.marketing_opt_in_at && (
              <VisionCard mt={6}>
                <Flex align="center" gap={3} mb={6}>
                  <IconBox variant="glass" size="md">
                    <FiMail fontSize="20px" color={sc.iconColor} />
                  </IconBox>
                  <Heading size="lg" color={sc.heading}>
                    {t("readers:marketing.title")}
                  </Heading>
                </Flex>
                <VStack gap={4} align="stretch">
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("readers:marketing.consent")}
                    </Text>
                    <Badge
                      colorScheme={
                        (reader as any).marketing_opt_in ? "green" : "gray"
                      }
                      size="sm"
                      mt={1}
                    >
                      {(reader as any).marketing_opt_in
                        ? t("readers:marketing.optedIn")
                        : t("readers:marketing.optedOut")}
                    </Badge>
                  </Box>
                  {(reader as any).marketing_email && (
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("readers:marketing.email")}
                      </Text>
                      <Text
                        color={sc.heading}
                        fontFamily="mono"
                        fontSize="sm"
                        mt={1}
                        wordBreak="break-all"
                      >
                        {(reader as any).marketing_email}
                      </Text>
                    </Box>
                  )}
                  <Box>
                    <Text
                      fontSize="xs"
                      color={sc.textMuted}
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t("readers:marketing.recordedAt")}
                    </Text>
                    <Text color={sc.heading} fontSize="sm" mt={1}>
                      {new Date(
                        (reader as any).marketing_opt_in_at,
                      ).toLocaleString()}
                    </Text>
                  </Box>
                  {(reader as any).marketing_opt_in_source && (
                    <Box>
                      <Text
                        fontSize="xs"
                        color={sc.textMuted}
                        textTransform="uppercase"
                        letterSpacing="wider"
                      >
                        {t("readers:marketing.source")}
                      </Text>
                      <Text
                        color={sc.textSecondary}
                        fontFamily="mono"
                        fontSize="sm"
                        mt={1}
                      >
                        {(reader as any).marketing_opt_in_source}
                      </Text>
                    </Box>
                  )}
                </VStack>
              </VisionCard>
            )}
        </Box>
      </Grid>

      <ConfirmDialog
        open={confirmRegenerate}
        onClose={() => setConfirmRegenerate(false)}
        onConfirm={() => regenerateCredentialsMutation.mutate()}
        title={t("readers:mqtt.regenerate")}
        message={t("readers:mqtt.confirmRegenerate")}
        loading={regenerateCredentialsMutation.isPending}
      />
      <ConfirmDialog
        open={confirmRevoke}
        onClose={() => setConfirmRevoke(false)}
        onConfirm={() => revokeCredentialsMutation.mutate()}
        title={t("readers:mqtt.revoke")}
        message={t("readers:mqtt.confirmRevoke")}
        loading={revokeCredentialsMutation.isPending}
      />

      {/* Fullscreen QR Code Dialog */}
      <DialogRoot
        open={!!fullscreenQrValue}
        onOpenChange={() => setFullscreenQrValue(null)}
        size="full"
      >
        <DialogBackdrop />
        <DialogContent
          bg="white"
          display="flex"
          alignItems="center"
          justifyContent="center"
          onClick={() => setFullscreenQrValue(null)}
        >
          <DialogCloseTrigger color="gray.500" />
          <DialogBody
            display="flex"
            alignItems="center"
            justifyContent="center"
            p={8}
          >
            {fullscreenQrValue && (
              <QRCodeSVG
                value={fullscreenQrValue}
                size={Math.min(window.innerWidth, window.innerHeight) * 0.75}
                level="M"
              />
            )}
          </DialogBody>
        </DialogContent>
      </DialogRoot>
    </Box>
  )
}

// ---------------------------------------------------------------------------
// Combined provisioning wizard (license request → reader → single QR)
// ---------------------------------------------------------------------------

interface WizardLicense {
  id: string
  key: string
  device_id: string
  product: string | null
  product_en: string | null
  status: string
  max_tags: number
  expires_at: string | null
  days_remaining?: number
  is_trial: boolean
  is_activated: boolean
  is_subscription: boolean
}

interface WizardRequest {
  id: string
  device_id: string
  mac_address: string | null
  email: string | null
  name: string | null
  product: string | null
  app_version: string | null
  reader_model: string | null
  tier: string | null
  requested_max_tags: number | null
  status: string
  created_at: string | null
}

async function wizardApiFetch(
  path: string,
  init?: RequestInit,
): Promise<Response> {
  const token = localStorage.getItem("access_token")
  return fetch(`${OpenAPI.BASE}${path}`, {
    ...init,
    headers: {
      ...(init?.headers ?? {}),
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
  })
}

// The single combined envelope QDatDroid scans to provision in one go:
// license + MQTT credentials + the devices/<topic_id>/{command,response,data}
// topics. See docs/standards/QR_ENVELOPE_CONVENTION.md (§5).
function buildProvisioningQr(license: WizardLicense, creds: any): string {
  return JSON.stringify({
    type: "qdat-provisioning",
    version: "1.0",
    issued_at: new Date().toISOString(),
    license: {
      key: license.key,
      device_id: license.device_id,
      product: license.product || "QDatDroid",
      product_en: license.product_en || "QDatDroid",
      status: license.status,
      max_tags: license.max_tags,
      expires_at: license.expires_at,
      days_remaining: license.days_remaining ?? 0,
      is_trial: license.is_trial,
      is_activated: license.is_activated,
      is_subscription: license.is_subscription,
      api_base: OpenAPI.BASE,
    },
    mqtt: {
      host: MQTT_BROKER_HOST,
      port: MQTT_BROKER_PORT,
      wss_port: MQTT_BROKER_WSS_PORT,
      tls: true,
      username: creds.username,
      ...(creds.password && { password: creds.password }),
    },
    device: {
      topic_id: creds.mqtt_topic_id,
      topics: creds.topics,
    },
  })
}

function ProvisionDeviceWizard({ requestId }: { requestId: string }) {
  const { t } = useTranslation(["readers", "common", "admin"])
  const navigate = useNavigate()
  const sc = useSemanticColors()
  const showToast = useCustomToast()
  const queryClient = useQueryClient()
  const { currentOrganization } = useContext(OrganizationContext)

  const [step, setStep] = useState<1 | 2 | 3>(1)
  const [license, setLicense] = useState<WizardLicense | null>(null)
  const [creds, setCreds] = useState<any>(null)

  // Step 1 — approval terms.
  const [approveMaxTags, setApproveMaxTags] = useState("0")
  const [approveValidDays, setApproveValidDays] = useState("")
  const [approveSubscription, setApproveSubscription] = useState(false)

  // Step 2 — reader details.
  const [readerName, setReaderName] = useState("")
  const [readerRole, setReaderRole] = useState<ReaderRoleEnum>("INVENTORY")

  const { data: request, isLoading: requestLoading } = useQuery({
    queryKey: ["license-request", requestId],
    queryFn: async () => {
      const res = await wizardApiFetch(`/api/v1/licenses/requests/${requestId}`)
      if (!res.ok) throw new Error("Failed to load license request")
      return res.json() as Promise<WizardRequest>
    },
  })

  useEffect(() => {
    if (!request) return
    if (request.requested_max_tags != null)
      setApproveMaxTags(String(request.requested_max_tags))
    setReaderName(
      request.name?.trim() || `QDatDroid ${request.device_id.slice(0, 8)}`,
    )
  }, [request])

  const approveMutation = useMutation({
    mutationFn: async () => {
      const res = await wizardApiFetch(
        `/api/v1/licenses/requests/${requestId}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            max_tags: Number(approveMaxTags) || 0,
            valid_days:
              approveValidDays === "" ? null : Number(approveValidDays),
            is_subscription: approveSubscription,
            product: "QDatDroid",
            product_en: "QDatDroid",
          }),
        },
      )
      if (!res.ok) {
        const err = await res.json().catch(() => ({}))
        throw new Error(err.detail || "Failed to approve request")
      }
      return res.json() as Promise<WizardLicense>
    },
    onSuccess: (lic) => {
      setLicense(lic)
      queryClient.invalidateQueries({ queryKey: ["licenses"] })
      queryClient.invalidateQueries({ queryKey: ["license-requests"] })
      setStep(2)
    },
    onError: (e: Error) => showToast.showErrorToast(e.message),
  })

  // A reader may already own this device id (e.g. the device had a prior
  // trial-provisioned reader). On the 409 we offer to re-bind that reader to
  // the new license instead of failing — see rebindMutation below.
  const [rebindOpen, setRebindOpen] = useState(false)
  const [conflictDetail, setConflictDetail] = useState("")
  // Tap the step-3 QR to blow it up to a full-screen overlay for easy scanning.
  const [qrFullscreen, setQrFullscreen] = useState(false)

  // Android Device ID used as a quasi-MAC (column is plain text, no format
  // validation); model is locked to QDATDROID. `rebind=true` re-uses an
  // existing reader for the same device id instead of 409-ing.
  const createReaderAndCreds = async (rebind: boolean) => {
    if (!license) throw new Error("No license")
    if (!currentOrganization || isAllOrgs(currentOrganization))
      throw new Error(t("common:org.selectSpecific"))
    const res = await wizardApiFetch(
      `/api/v1/readers/?rebind=${rebind ? "true" : "false"}`,
      {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          organisation_id: currentOrganization.id,
          name: readerName,
          device_model: "QDATDROID",
          desired_state: "STANDBY",
          role: readerRole,
          mac_address: license.device_id,
        }),
      },
    )
    if (!res.ok) {
      const err = await res.json().catch(() => ({}))
      const e: any = new Error(err.detail || "Failed to create reader")
      e.status = res.status
      throw e
    }
    const reader = (await res.json()) as { id: string }
    // A re-bound reader may already hold MQTT credentials (generate would 409),
    // so regenerate — it revokes-then-issues, and is a no-op revoke when none
    // exist. A freshly created reader has none, so plain generate is correct.
    return rebind
      ? ReadersService.regenerateMqttCredentials({ id: reader.id })
      : ReadersService.generateMqttCredentials({ id: reader.id })
  }

  const createMutation = useMutation({
    mutationFn: () => createReaderAndCreds(false),
    onSuccess: (c) => {
      setCreds(c)
      queryClient.invalidateQueries({ queryKey: ["readers"] })
      setStep(3)
    },
    onError: (err: any) => {
      // 409 = a reader already owns this device id → offer to re-bind it.
      if (err?.status === 409) {
        setConflictDetail(err.message)
        setRebindOpen(true)
        return
      }
      showToast.showErrorToast(err.message || "Failed to create reader")
    },
  })

  const rebindMutation = useMutation({
    mutationFn: () => createReaderAndCreds(true),
    onSuccess: (c) => {
      setCreds(c)
      queryClient.invalidateQueries({ queryKey: ["readers"] })
      setRebindOpen(false)
      setStep(3)
    },
    onError: (err: any) => {
      setRebindOpen(false)
      showToast.showErrorToast(err.message || "Failed to re-bind reader")
    },
  })

  const copyQr = () => {
    if (!license || !creds) return
    navigator.clipboard.writeText(buildProvisioningQr(license, creds))
    showToast.showSuccessToast(t("readers:mqtt.copied", { label: "QR" }))
  }

  const stepLabels = [
    t("readers:provision.step1"),
    t("readers:provision.step2"),
    t("readers:provision.step3"),
  ]

  return (
    <Box p={{ base: 4, md: 8 }} maxW="3xl" mx="auto">
      <VisionButton
        variant="ghost"
        size="sm"
        mb={4}
        onClick={() => navigate({ to: "/licenses" })}
      >
        <FiArrowLeft /> {t("readers:provision.backToLicenses")}
      </VisionButton>

      <Heading size="lg" color={sc.heading} mb={1}>
        {t("readers:provision.title")}
      </Heading>
      <Text color={sc.textMuted} mb={6}>
        {t("readers:provision.subtitle")}
      </Text>

      {/* Stepper */}
      <HStack gap={3} mb={6}>
        {stepLabels.map((label, i) => {
          const n = (i + 1) as 1 | 2 | 3
          const active = step === n
          const done = step > n
          return (
            <HStack key={label} gap={2} opacity={active || done ? 1 : 0.5}>
              <Badge colorPalette={done ? "green" : active ? "blue" : "gray"}>
                {done ? <FiCheckCircle /> : n}
              </Badge>
              <Text
                color={sc.text}
                fontSize="sm"
                fontWeight={active ? 600 : 400}
              >
                {label}
              </Text>
            </HStack>
          )
        })}
      </HStack>

      {requestLoading ? (
        <Flex justify="center" py={10}>
          <Spinner size="lg" color={sc.spinnerColor} />
        </Flex>
      ) : !request ? (
        <Text color={sc.textMuted}>{t("readers:provision.notFound")}</Text>
      ) : (
        <VisionCard p={6}>
          {/* Step 1 — respond to request */}
          {step === 1 && (
            <VStack align="stretch" gap={4}>
              <Heading size="md" color={sc.heading}>
                {t("readers:provision.step1Title")}
              </Heading>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                <Box>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("admin:licenses.deviceId")}
                  </Text>
                  <Text color={sc.text} fontFamily="mono">
                    {request.device_id}
                  </Text>
                </Box>
                <Box>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("readers:provision.requestedAt")}
                  </Text>
                  <Text color={sc.text}>
                    {request.created_at
                      ? new Date(request.created_at).toLocaleString()
                      : "—"}
                  </Text>
                </Box>
                <Box>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("admin:licenses.colCustomer")}
                  </Text>
                  <Text color={sc.text}>
                    {request.name || request.email || "—"}
                  </Text>
                </Box>
                <Box>
                  <Text color={sc.textMuted} fontSize="xs">
                    {t("admin:licenses.colAppVersion")}
                  </Text>
                  <Text color={sc.text}>{request.app_version || "—"}</Text>
                </Box>
              </Grid>

              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                <Field label={t("admin:licenses.maxTags")}>
                  <VisionInput
                    type="number"
                    value={approveMaxTags}
                    onChange={(e) => setApproveMaxTags(e.target.value)}
                  />
                </Field>
                <Field
                  label={t("admin:licenses.validDays")}
                  helperText={t("readers:provision.validDaysHint")}
                >
                  <VisionInput
                    type="number"
                    value={approveValidDays}
                    placeholder={t("admin:licenses.perpetual")}
                    onChange={(e) => setApproveValidDays(e.target.value)}
                  />
                </Field>
              </Grid>
              <label style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <input
                  type="checkbox"
                  checked={approveSubscription}
                  onChange={(e) => setApproveSubscription(e.target.checked)}
                />
                <Text color={sc.text} fontSize="sm">
                  {t("admin:licenses.isSubscription")}
                </Text>
              </label>

              <HStack justify="flex-end">
                <VisionButton
                  variant="brand"
                  loading={approveMutation.isPending}
                  onClick={() => approveMutation.mutate()}
                >
                  <FiKey /> {t("readers:provision.approveContinue")}{" "}
                  <FiArrowRight />
                </VisionButton>
              </HStack>
            </VStack>
          )}

          {/* Step 2 — create reader */}
          {step === 2 && license && (
            <VStack align="stretch" gap={4}>
              <Heading size="md" color={sc.heading}>
                {t("readers:provision.step2Title")}
              </Heading>
              <Text color={sc.textMuted} fontSize="sm">
                {t("readers:provision.step2Hint")}
              </Text>
              <Field label={t("readers:form.name")}>
                <VisionInput
                  value={readerName}
                  onChange={(e) => setReaderName(e.target.value)}
                />
              </Field>
              <Grid templateColumns={{ base: "1fr", md: "1fr 1fr" }} gap={3}>
                <Field label={t("readers:form.model")}>
                  <VisionInput value="QDatDroid" readOnly disabled />
                </Field>
                <Field
                  label={t("readers:provision.deviceIdAsMac")}
                  helperText={t("readers:provision.deviceIdAsMacHint")}
                >
                  <VisionInput
                    value={license.device_id}
                    readOnly
                    disabled
                    fontFamily="mono"
                  />
                </Field>
              </Grid>
              <Field label={t("readers:form.role")}>
                <select
                  value={readerRole}
                  onChange={(e) =>
                    setReaderRole(e.target.value as ReaderRoleEnum)
                  }
                  style={{
                    width: "100%",
                    padding: "8px 12px",
                    borderRadius: 8,
                    background: "transparent",
                    color: "inherit",
                    border: `1px solid ${sc.border}`,
                  }}
                >
                  {readerRoles.map((r) => (
                    <option key={r.value} value={r.value}>
                      {r.label}
                    </option>
                  ))}
                </select>
              </Field>
              <HStack justify="space-between">
                <VisionButton variant="ghost" onClick={() => setStep(1)}>
                  <FiArrowLeft /> {t("common:actions.back")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  loading={createMutation.isPending}
                  onClick={() => createMutation.mutate()}
                >
                  <FiWifi /> {t("readers:provision.createProvision")}{" "}
                  <FiArrowRight />
                </VisionButton>
              </HStack>
            </VStack>
          )}

          {/* Step 3 — combined QR */}
          {step === 3 && license && creds && (
            <VStack align="stretch" gap={4}>
              <Heading size="md" color={sc.heading}>
                <FiSmartphone style={{ display: "inline", marginRight: 8 }} />
                {t("readers:provision.step3Title")}
              </Heading>
              <Text color={sc.textMuted} fontSize="sm">
                {t("readers:provision.step3Hint")}
              </Text>
              <VStack gap={1} py={2}>
                <Box
                  bg="white"
                  p={4}
                  borderRadius="lg"
                  cursor="pointer"
                  transition="transform 0.15s"
                  _hover={{ transform: "scale(1.02)" }}
                  onClick={() => setQrFullscreen(true)}
                  title={t("readers:provision.qrEnlargeHint")}
                >
                  <QRCodeSVG
                    value={buildProvisioningQr(license, creds)}
                    size={320}
                    level="Q"
                  />
                </Box>
                <Text color={sc.textMuted} fontSize="xs">
                  {t("readers:provision.qrEnlargeHint")}
                </Text>
              </VStack>
              <VStack
                align="stretch"
                gap={1}
                fontSize="sm"
                fontFamily="mono"
                color={sc.text}
              >
                <Text>
                  {t("admin:licenses.colKey")}: {license.key}
                </Text>
                <Text>
                  {t("admin:licenses.deviceId")}: {license.device_id}
                </Text>
                <Text>
                  {t("admin:licenses.maxTags")}: {license.max_tags}
                </Text>
                <Text>
                  {t("readers:mqtt.username")}: {creds.username}
                </Text>
                <Text>
                  {t("readers:mqtt.topicId")}: {creds.mqtt_topic_id}
                </Text>
                <Text color={sc.textMuted}>{creds.topics?.command}</Text>
                <Text color={sc.textMuted}>{creds.topics?.response}</Text>
                <Text color={sc.textMuted}>{creds.topics?.data}</Text>
              </VStack>
              <HStack justify="space-between">
                <VisionButton variant="ghost" onClick={copyQr}>
                  <FiCopy /> {t("readers:mqtt.copyJson")}
                </VisionButton>
                <VisionButton
                  variant="brand"
                  onClick={() => navigate({ to: "/readers" })}
                >
                  <FiCheckCircle /> {t("common:actions.done")}
                </VisionButton>
              </HStack>
            </VStack>
          )}
        </VisionCard>
      )}

      {qrFullscreen && license && creds && (
        <Flex
          position="fixed"
          inset={0}
          zIndex={2000}
          bg="rgba(0, 0, 0, 0.92)"
          align="center"
          justify="center"
          p={4}
          onClick={() => setQrFullscreen(false)}
        >
          <VStack
            gap={6}
            w="min(88vw, 88vh)"
            onClick={(e) => e.stopPropagation()}
          >
            <Box bg="white" p={{ base: 4, md: 6 }} borderRadius="2xl" w="100%">
              <QRCodeSVG
                value={buildProvisioningQr(license, creds)}
                size={1024}
                level="Q"
                style={{ width: "100%", height: "auto", display: "block" }}
              />
            </Box>
            <VisionButton
              variant="brand"
              size="lg"
              width="100%"
              onClick={() => setQrFullscreen(false)}
            >
              <FiArrowLeft /> {t("readers:provision.qrReturn")}
            </VisionButton>
          </VStack>
        </Flex>
      )}

      <ConfirmDialog
        open={rebindOpen}
        onClose={() => setRebindOpen(false)}
        onConfirm={() => rebindMutation.mutate()}
        title={t("readers:provision.rebindTitle")}
        message={t("readers:provision.rebindMessage", {
          detail: conflictDetail,
        })}
        confirmLabel={t("readers:provision.rebindConfirm")}
        colorScheme="orange"
        loading={rebindMutation.isPending}
      />
    </Box>
  )
}
